import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Combobox } from "@/components/cinch/Combobox";
import { Field } from "@/components/cinch/Field";
import { MarkdownNotes } from "@/components/cinch/MarkdownNotes/MarkdownNotes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";

import {
  type GlossaryTerm,
  type TermInput,
  type TermKind,
  useCreateTerm,
  useDeleteTerm,
  useUpdateTerm,
} from "../api";

const KINDS: { key: TermKind; label: string }[] = [
  { key: "concept", label: "Concept" },
  { key: "metric", label: "Metric" },
  { key: "calculation", label: "Calculation" },
  { key: "query", label: "Query" },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 128);
}

export interface TermEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create mode. */
  term: GlossaryTerm | null;
  allTerms: GlossaryTerm[];
  /** Every tag already in use, for add-tag suggestions. */
  allTags: string[];
}

export function TermEditDialog({
  open,
  onOpenChange,
  term,
  allTerms,
  allTags,
}: TermEditDialogProps) {
  const isEdit = term !== null;
  const create = useCreateTerm();
  const update = useUpdateTerm();
  const del = useDeleteTerm();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [kinds, setKinds] = useState<TermKind[]>([]);
  const [definition, setDefinition] = useState("");
  const [equation, setEquation] = useState("");
  const [query, setQuery] = useState("");
  const [aliases, setAliases] = useState("");
  const [related, setRelated] = useState<string[]>([]);

  // Reset the form whenever the dialog opens for a (different) term.
  useEffect(() => {
    if (!open) return;
    setName(term?.term ?? "");
    setSlug(term?.slug ?? "");
    setSlugTouched(isEdit);
    setTags(term?.tags ?? []);
    setTagInput("");
    setKinds(term?.kinds ?? []);
    setDefinition(term?.definition ?? "");
    setEquation(term?.equation ?? "");
    setQuery(term?.query ?? "");
    setAliases((term?.aliases ?? []).join(", "));
    setRelated(term?.related ?? []);
  }, [open, term, isEdit]);

  const addTag = (raw: string) => {
    const t = raw.trim();
    if (t && !tags.includes(t)) setTags((cur) => [...cur, t]);
    setTagInput("");
  };

  // Auto-fill slug from the name until the user edits it directly.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const relatedOptions = useMemo(
    () =>
      allTerms
        .filter((t) => t.slug !== slug && !related.includes(t.slug))
        .map((t) => ({ id: t.slug, label: t.term, subtitle: t.category })),
    [allTerms, slug, related],
  );
  const termBySlug = useMemo(
    () => new Map(allTerms.map((t) => [t.slug, t] as const)),
    [allTerms],
  );

  const toggleKind = (k: TermKind) =>
    setKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const canSave = name.trim() && slug.trim() && definition.trim();
  const busy = create.isPending || update.isPending || del.isPending;

  const save = async () => {
    const body: TermInput = {
      slug: slug.trim(),
      term: name.trim(),
      aliases: aliases.trim()
        ? aliases.split(",").map((a) => a.trim()).filter(Boolean)
        : null,
      // Category is no longer surfaced in the UI (the list is flat); preserve
      // an existing value, default new terms to "General".
      category: term?.category ?? "General",
      tags,
      kinds,
      definition: definition.trim(),
      equation: equation.trim() || null,
      query: query.trim() || null,
      sources: null,
      related: related.length ? related : null,
      sort_order: term?.sort_order ?? 0,
    };
    try {
      if (isEdit && term) {
        await update.mutateAsync({ id: term.id, body });
        toast.success("Term updated");
      } else {
        await create.mutateAsync(body);
        toast.success("Term added");
      }
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Save failed";
      toast.error(msg);
    }
  };

  const remove = async () => {
    if (!term) return;
    try {
      await del.mutateAsync(term.id);
      toast.success("Term deleted");
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Delete failed";
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit term" : "Add term"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="term-name"
              label="Term"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              valid={!!name.trim()}
            />
            <Field
              id="term-slug"
              label="Slug (cross-link id)"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
              required
              valid={!!slug.trim()}
              helper="Lowercase, kebab-case. Used by Related links."
            />
          </div>

          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
              Kinds
            </div>
            <div className="flex flex-wrap gap-3">
              {KINDS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={kinds.includes(key)}
                    onCheckedChange={() => toggleKind(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <MarkdownNotes
            value={definition}
            onChange={setDefinition}
            label="Definition"
            placeholder="Plain-English definition (markdown supported)…"
            minHeightPx={110}
          />

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-wider text-muted-foreground">
              Equation <span className="normal-case text-muted-foreground/70">(optional)</span>
            </div>
            <Textarea
              value={equation}
              onChange={(e) => setEquation(e.target.value)}
              rows={2}
              placeholder="e.g. ARR = MRR × 12"
              className="font-mono text-xs"
              aria-label="Equation"
            />
          </div>

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-wider text-muted-foreground">
              Query <span className="normal-case text-muted-foreground/70">(how to reproduce it — optional)</span>
            </div>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              rows={4}
              placeholder="The exact query used — Snowflake SQL, a Cinch Ops query, or another source — that returns this number…"
              className="font-mono text-xs"
              aria-label="Query"
            />
          </div>

          <Field
            id="term-aliases"
            label="Aliases (comma-separated)"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
          />

          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
              Tags
            </div>
            {tags.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-2">
                {tags.map((t) => (
                  <Badge key={t} variant="neutral" className="gap-1">
                    {t}
                    <button
                      type="button"
                      onClick={() => setTags((cur) => cur.filter((x) => x !== t))}
                      className="ml-0.5 rounded-full hover:text-danger"
                      aria-label={`Remove ${t}`}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
            <Field
              id="term-tags"
              label="Add a tag (Enter)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag(tagInput);
                }
              }}
              list="glossary-tag-suggestions"
              helper="Group terms however you like — e.g. Revenue, Sales, Finance, Retention."
            />
            <datalist id="glossary-tag-suggestions">
              {allTags.filter((t) => !tags.includes(t)).map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>

          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
              Related terms
            </div>
            {related.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-2">
                {related.map((s) => (
                  <Badge key={s} variant="info" className="gap-1">
                    {termBySlug.get(s)?.term ?? s}
                    <button
                      type="button"
                      onClick={() => setRelated((cur) => cur.filter((x) => x !== s))}
                      className="ml-0.5 rounded-full hover:text-danger"
                      aria-label={`Remove ${s}`}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
            <Combobox
              options={relatedOptions}
              value={null}
              onChange={(id) => id && setRelated((cur) => [...cur, id])}
              placeholder="Add a related term…"
              searchPlaceholder="Search terms…"
            />
          </div>
        </div>

        {isEdit && term?.status === "approved" ? (
          <p className="text-xs text-warning">
            Saving edits will send this term back to <strong>In Review</strong> until a
            glossary admin approves it again.
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          {isEdit ? (
            <Button variant="destructive" onClick={remove} disabled={busy}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} disabled={!canSave || busy}>
              {isEdit ? "Save changes" : "Add term"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
